import React, { useCallback, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { type Company, fetchCompanies } from "../../services/adminApi";
import {
  type CustomReportAdmin,
  type CustomReportKind,
  createCustomReport,
  deleteCustomReport,
  fetchCustomReportKinds,
  fetchCustomReportsAdmin,
  updateCustomReport,
} from "../../services/customReportsApi";

/**
 * Settings → Custom Reports (platform + users.manage). Build a report from a
 * pre-approved KIND (no free SQL), preset its params, target a customer (or
 * all), and toggle visibility. Running is scoped server-side to each viewer.
 */
const CustomReportsTab: React.FC = () => {
  const { token } = useAuth();
  const [reports, setReports] = useState<CustomReportAdmin[]>([]);
  const [kinds, setKinds] = useState<CustomReportKind[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // create/edit form
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("");
  const [params, setParams] = useState<Record<string, string>>({});
  const [companyId, setCompanyId] = useState<string>("");
  const [visible, setVisible] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!token) return;
    try {
      const [r, k, c] = await Promise.all([
        fetchCustomReportsAdmin(token),
        fetchCustomReportKinds(token),
        fetchCompanies(token),
      ]);
      setReports(r.reports);
      setKinds(k.kinds);
      setCompanies(c.filter((x) => x.company_type === "customer"));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const resetForm = (): void => {
    setEditId(null); setName(""); setKind(""); setParams({});
    setCompanyId(""); setVisible(false);
  };

  const startEdit = (r: CustomReportAdmin): void => {
    setEditId(r.report_id);
    setName(r.name);
    setKind(r.report_kind);
    setParams(
      Object.fromEntries(Object.entries(r.params ?? {}).map(([k, v]) => [k, String(v ?? "")])),
    );
    setCompanyId(r.company_id != null ? String(r.company_id) : "");
    setVisible(r.is_visible);
  };

  const selectedKind = kinds.find((k) => k.kind === kind);

  const save = async (): Promise<void> => {
    if (!token || !name.trim() || !kind) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        report_kind: kind,
        params,
        company_id: companyId ? Number(companyId) : null,
        is_visible: visible,
      };
      if (editId == null) {
        await createCustomReport(token, body);
      } else {
        await updateCustomReport(token, editId, body);
      }
      resetForm();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleVisible = async (r: CustomReportAdmin): Promise<void> => {
    if (!token) return;
    try {
      await updateCustomReport(token, r.report_id, { is_visible: !r.is_visible });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async (r: CustomReportAdmin): Promise<void> => {
    if (!token || !window.confirm(`Delete custom report "${r.name}"?`)) return;
    try {
      await deleteCustomReport(token, r.report_id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const companyName = (cid: number | null): string =>
    cid == null ? "All customers" : (companies.find((c) => c.company_id === cid)?.company_name ?? `#${cid}`);

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-secondary-600">
        Custom reports are built from pre-approved report types (no free SQL) and
        run scoped to each viewer's own boxes. Built-in reports are unaffected and
        stay visible to everyone; these are hidden until you toggle them on.
      </p>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {/* existing reports */}
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-1.5">Name</th>
              <th className="text-left px-3 py-1.5">Type</th>
              <th className="text-left px-3 py-1.5">Audience</th>
              <th className="text-left px-3 py-1.5">Visible</th>
              <th className="px-3 py-1.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {reports.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-4 text-secondary-500">No custom reports yet.</td></tr>
            ) : reports.map((r) => (
              <tr key={r.report_id}>
                <td className="px-3 py-1.5 font-medium">{r.name}</td>
                <td className="px-3 py-1.5 text-secondary-600">
                  {kinds.find((k) => k.kind === r.report_kind)?.label ?? r.report_kind}
                </td>
                <td className="px-3 py-1.5 text-secondary-600">{companyName(r.company_id)}</td>
                <td className="px-3 py-1.5">
                  <button
                    type="button"
                    onClick={() => void toggleVisible(r)}
                    className={
                      "text-[11px] px-2 py-0.5 rounded-full " +
                      (r.is_visible ? "bg-success-100 text-success-800" : "bg-secondary-100 text-secondary-600")
                    }
                  >
                    {r.is_visible ? "visible" : "hidden"}
                  </button>
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button type="button" className="text-primary-700 hover:underline text-xs mr-3"
                    onClick={() => startEdit(r)}>edit</button>
                  <button type="button" className="text-error-700 hover:underline text-xs"
                    onClick={() => void remove(r)}>delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* create / edit form */}
      <div className="bg-white border border-secondary-200 rounded-md p-4 space-y-3">
        <h3 className="text-sm font-semibold">
          {editId == null ? "New custom report" : `Editing "${name}"`}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-xs text-secondary-500 mb-0.5">Name</span>
            <input className="w-full border border-secondary-300 rounded px-2 py-1.5"
              value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-secondary-500 mb-0.5">Report type</span>
            <select className="w-full border border-secondary-300 rounded px-2 py-1.5 bg-white"
              value={kind} onChange={(e) => { setKind(e.target.value); setParams({}); }}>
              <option value="">— pick a type —</option>
              {kinds.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
            </select>
          </label>
        </div>

        {selectedKind && (
          <>
            <p className="text-xs text-secondary-500">{selectedKind.description}</p>
            {selectedKind.params.map((p) => (
              <label key={p.key} className="text-sm block">
                <span className="block text-xs text-secondary-500 mb-0.5">
                  {p.label}{p.required ? " *" : ""}
                </span>
                <input className="w-full border border-secondary-300 rounded px-2 py-1.5"
                  value={params[p.key] ?? ""}
                  onChange={(e) => setParams((prev) => ({ ...prev, [p.key]: e.target.value }))} />
              </label>
            ))}
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <label className="text-sm">
            <span className="block text-xs text-secondary-500 mb-0.5">Audience</span>
            <select className="w-full border border-secondary-300 rounded px-2 py-1.5 bg-white"
              value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">All customers</option>
              {companies.map((c) => (
                <option key={c.company_id ?? 0} value={String(c.company_id)}>{c.company_name}</option>
              ))}
            </select>
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
            <span>Visible to customers</span>
          </label>
        </div>

        <div className="flex gap-2">
          <Button type="button" size="sm" disabled={busy || !name.trim() || !kind}
            onClick={() => void save()}>
            {busy ? "Saving…" : editId == null ? "Create report" : "Save changes"}
          </Button>
          {editId != null && (
            <Button type="button" size="sm" variant="outline" onClick={resetForm}>Cancel</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomReportsTab;
